import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { assistantCronService, assistantService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { AssistantCron, AssistantCronFilters } from '../types/assistantCron';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import AssistantCronList from '../components/assistant-crons/AssistantCronList';
import AssistantCronForm from '../components/assistant-crons/AssistantCronForm';
import AssistantCronFiltersBar from '../components/assistant-crons/AssistantCronFilters';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function AssistantCrons() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingCron, setEditingCron] = useState<AssistantCron | null>(null);
  const [filters, setFilters] = useState<AssistantCronFilters>({});
  const queryClient = useQueryClient();

  const { data: cronData, isLoading } = useQuery({
    queryKey: ['assistant-crons', filters],
    queryFn: () => assistantCronService.list(filters),
  });

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<AssistantCron>) => assistantCronService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant-crons'] });
      messageHandler.success('Cron criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: AssistantCron) => assistantCronService.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant-crons'] });
      messageHandler.success('Cron atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => assistantCronService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant-crons'] });
      messageHandler.success('Cron excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (data: any) => {
    const processedData = {
      ...data,
      assistant_id: Number(data.assistant_id),
      interval_minutes: Number(data.interval_minutes),
      enabled: data.enabled ?? true,
      filter_min_tickets_pending: data.filter_min_tickets_pending
        ? Number(data.filter_min_tickets_pending)
        : null,
    };

    if (editingCron) {
      updateMutation.mutate({ ...editingCron, ...processedData });
    } else {
      createMutation.mutate(processedData);
    }
  };

  const handleDelete = (cron: AssistantCron) => {
    messageHandler.confirm(
      'Excluir Cron',
      'Tem certeza que deseja excluir este cron?',
      () => {
        deleteMutation.mutate(cron.id);
        closeModal();
      }
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingCron(null);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters =
    filters.assistant_id !== undefined ||
    filters.enabled !== undefined ||
    filters.name !== undefined ||
    filters.last_status !== undefined;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Crons de Assistentes"
          description="Gerencie tarefas agendadas para seus assistentes."
          actions={
            <Button
              onClick={() => {
                setEditingCron(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Cron
            </Button>
          }
        />

        <AssistantCronFiltersBar
          filters={filters}
          assistants={assistants || []}
          onChange={setFilters}
          onClear={handleClearFilters}
        />

        <AssistantCronList
          crons={cronData?.data || []}
          onEdit={cron => {
            setEditingCron(cron);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
          isFiltered={hasActiveFilters}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingCron ? 'Editar Cron' : 'Novo Cron'}
          size="lg"
        >
          <AssistantCronForm
            cron={editingCron}
            assistants={assistants || []}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default AssistantCrons;
